import { Portal, Box, useDisclosure } from "@chakra-ui/react";
import Footer from "components/footer/FooterAdmin.js";
import Navbar from "components/navbar/NavbarAdmin.js";
import Sidebar from "components/sidebar/Sidebar.js";
import { SidebarContext } from "contexts/SidebarContext";
import React, { useMemo, useState } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import routes from "routes.js";
import { getCurrentUserRole } from "Api/apiService";

const isAllowedRoute = (route, role) => {
  if (!route.allowedRoles || route.allowedRoles.length === 0) {
    return true;
  }
  return route.allowedRoles.includes(role);
};

const filterRoutesByRole = (routeList, role) => {
  return (routeList || []).reduce((acc, route) => {
    if (route.category || route.collapse) {
      if (!isAllowedRoute(route, role)) {
        return acc;
      }
      const filteredItems = filterRoutesByRole(route.items || [], role);
      if (filteredItems.length > 0) {
        acc.push({ ...route, items: filteredItems });
      }
      return acc;
    }

    if (isAllowedRoute(route, role)) {
      acc.push(route);
    }
    return acc;
  }, []);
};

const findFirstAdminRoute = (routeList) => {
  for (const route of routeList || []) {
    if ((route.category || route.collapse) && route.items) {
      const nested = findFirstAdminRoute(route.items);
      if (nested) {
        return nested;
      }
      continue;
    }

    if (route.layout === "/admin") {
      return `${route.layout}${route.path}`;
    }
  }

  return "/auth/sign-in";
};

export default function Dashboard(props) {
  const { ...rest } = props;
  const [fixed] = useState(false);
  const [toggleSidebar, setToggleSidebar] = useState(false);

  const userRole = getCurrentUserRole();
  const filteredRoutes = useMemo(
    () => filterRoutesByRole(routes, userRole),
    [userRole]
  );
  const defaultAdminRoute = useMemo(
    () => findFirstAdminRoute(filteredRoutes),
    [filteredRoutes]
  );

  const getRoute = () => {
    return window.location.pathname !== "/admin/full-screen-maps";
  };

  const getActiveRoute = (routeList) => {
    let activeRoute = "Default Brand Text";
    for (let i = 0; i < routeList.length; i++) {
      if (routeList[i].collapse || routeList[i].category) {
        const nestedActiveRoute = getActiveRoute(routeList[i].items || []);
        if (nestedActiveRoute !== activeRoute) {
          return nestedActiveRoute;
        }
      } else if (
        window.location.href.indexOf(routeList[i].layout + routeList[i].path) !== -1
      ) {
        return routeList[i].name;
      }
    }
    return activeRoute;
  };

  const getActiveNavbar = (routeList) => {
    let activeNavbar = false;
    for (let i = 0; i < routeList.length; i++) {
      if (routeList[i].collapse || routeList[i].category) {
        const nestedActiveNavbar = getActiveNavbar(routeList[i].items || []);
        if (nestedActiveNavbar !== activeNavbar) {
          return nestedActiveNavbar;
        }
      } else if (
        window.location.href.indexOf(routeList[i].layout + routeList[i].path) !== -1
      ) {
        return routeList[i].secondary;
      }
    }
    return activeNavbar;
  };

  const getActiveNavbarText = (routeList) => {
    let activeNavbar = false;
    for (let i = 0; i < routeList.length; i++) {
      if (routeList[i].collapse || routeList[i].category) {
        const nestedActiveNavbar = getActiveNavbarText(routeList[i].items || []);
        if (nestedActiveNavbar !== activeNavbar) {
          return nestedActiveNavbar;
        }
      } else if (
        window.location.href.indexOf(routeList[i].layout + routeList[i].path) !== -1
      ) {
        return routeList[i].messageNavbar;
      }
    }
    return activeNavbar;
  };

  const getRoutes = (routeList) => {
    return routeList.map((prop, key) => {
      if (prop.layout === "/admin") {
        return (
          <Route
            path={prop.layout + prop.path}
            component={prop.component}
            key={key}
          />
        );
      }

      if (prop.collapse || prop.category) {
        return getRoutes(prop.items || []);
      }

      return null;
    });
  };

  document.documentElement.dir = "ltr";
  const { onOpen } = useDisclosure();

  return (
    <Box>
      <Box>
        <SidebarContext.Provider
          value={{
            toggleSidebar,
            setToggleSidebar,
          }}
        >
          <Sidebar routes={filteredRoutes} display="none" {...rest} />
          <Box
            float="right"
            minHeight="100vh"
            height="100%"
            overflow="auto"
            position="relative"
            maxHeight="100%"
            w={{ base: "100%", xl: "calc( 100% - 290px )" }}
            maxWidth={{ base: "100%", xl: "calc( 100% - 290px )" }}
            transition="all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)"
            transitionDuration=".2s, .2s, .35s"
            transitionProperty="top, bottom, width"
            transitionTimingFunction="linear, linear, ease"
          >
            <Portal>
              <Box>
                <Navbar
                  onOpen={onOpen}
                  logoText={"The Grove"}
                  brandText={getActiveRoute(filteredRoutes)}
                  secondary={getActiveNavbar(filteredRoutes)}
                  message={getActiveNavbarText(filteredRoutes)}
                  fixed={fixed}
                  {...rest}
                />
              </Box>
            </Portal>

            {getRoute() ? (
              <Box
                mx="auto"
                p={{ base: "20px", md: "30px" }}
                pe="20px"
                minH="100vh"
                pt="50px"
              >
                <Switch>
                  {getRoutes(filteredRoutes)}
                  <Redirect exact from="/admin" to={defaultAdminRoute} />
                  <Redirect from="/" to={defaultAdminRoute} />
                </Switch>
              </Box>
            ) : null}
            <Box>
              <Footer />
            </Box>
          </Box>
        </SidebarContext.Provider>
      </Box>
    </Box>
  );
}
